import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupsIcon from '@mui/icons-material/Groups';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { useAuth } from '../contexts/AuthContext';
import { fetchFriendActivities } from '../lib/friendActivity';
import FriendActivitySummary from './FriendActivitySummary';

const PREVIEW_LIMIT = 3;

export default function HomeFriendsPreview() {
  const { user, profile } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPreview = useCallback(async () => {
    if (!user?.id) return;

    const grouped = await fetchFriendActivities(user.id);
    setFriends(grouped.slice(0, PREVIEW_LIMIT));
  }, [user?.id]);

  useEffect(() => {
    loadPreview().finally(() => setLoading(false));
  }, [loadPreview]);

  const playingNowCount = friends.filter((friend) => friend.isPlayingNow).length;

  return (
    <Stack spacing={3} alignItems="stretch" className="w-full max-w-2xl">
      <Stack spacing={3} alignItems="center" className="text-center">
        <Chip
          icon={<GroupsIcon />}
          label="Friends on Steam"
          size="small"
          className="border border-indigo-500/30 bg-indigo-500/10 font-semibold text-indigo-300"
        />

        <div>
          <Typography
            variant="h2"
            component="h1"
            className="text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl"
          >
            Welcome back{profile?.display_name ? `, ${profile.display_name}` : ''}
          </Typography>
          <Typography variant="body1" className="mx-auto mt-3 max-w-lg text-slate-300">
            {friends.length > 0
              ? `Your friends have been active on Steam${playingNowCount > 0 ? ` — ${playingNowCount} playing now` : ''}.`
              : 'See what your GamingWithBros friends are playing once they sign up and sync.'}
          </Typography>
        </div>
      </Stack>

      <Card
        elevation={0}
        className="w-full border border-indigo-500/20 transition-colors hover:border-indigo-500/40"
      >
        <CardActionArea
          component={RouterLink}
          to="/friends"
          sx={{ textAlign: 'left', display: 'block' }}
        >
          <CardContent className="p-5 sm:p-6" sx={{ textAlign: 'left' }}>
            {loading ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={28} />
              </Stack>
            ) : friends.length === 0 ? (
              <Stack alignItems="center" spacing={1.5} py={4} textAlign="center">
                <SportsEsportsIcon className="text-4xl text-indigo-400" />
                <Typography className="text-slate-300">
                  No mutual friends on GamingWithBros yet. Invite Steam friends to sign in.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ width: '100%', textAlign: 'left' }}>
                {friends.map((friend) => (
                  <FriendActivitySummary key={friend.friendId} friend={friend} />
                ))}
              </Stack>
            )}

            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} className="mt-5 pt-2">
              <Typography className="font-semibold text-indigo-300">
                View all friends activity
              </Typography>
              <ArrowForwardIcon className="text-indigo-300" fontSize="small" />
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>

      <Button
        component={RouterLink}
        to="/friends"
        variant="contained"
        color="primary"
        size="large"
        endIcon={<ArrowForwardIcon />}
        className="mx-auto w-full sm:w-auto"
      >
        Go to Friends Activity
      </Button>
    </Stack>
  );
}
